import React, { useState, useEffect, useMemo } from 'react';
import {
  X, Phone, Mail, FileText, Calendar, StickyNote, Send, Plus,
  Trash2, Loader2, Sparkles, Download, ChevronDown, ChevronUp, Save,
  FileSignature, BookmarkPlus, Smartphone, Globe, Edit2,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import KnowledgePicker from './KnowledgePicker';
import OutcomePanel from './OutcomePanel';
import DocumentsSection from './DocumentsSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITY_TYPES = [
  { value: 'anruf',  label: 'Anruf',     Icon: Phone },
  { value: 'email',  label: 'E-Mail',    Icon: Mail },
  { value: 'termin', label: 'Termin',    Icon: Calendar },
  { value: 'brief',  label: 'Brief',     Icon: Send },
  { value: 'notiz',  label: 'Notiz',     Icon: StickyNote },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const LeadDetailModal = ({ leadId, password, onClose, onChange }) => {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);

  // Activity form
  const [actType, setActType] = useState('anruf');
  const [actContent, setActContent] = useState('');
  const [actDate, setActDate] = useState(todayISO());
  const [savingActivity, setSavingActivity] = useState(false);

  // Follow-up
  const [followUp, setFollowUp] = useState('');
  const [savingFollowUp, setSavingFollowUp] = useState(false);

  // Briefing
  const [bKeywords, setBKeywords] = useState('');
  const [bGoal, setBGoal] = useState('Erstgespräch vereinbaren');
  const [generating, setGenerating] = useState(false);
  const [openBriefingId, setOpenBriefingId] = useState(null);
  const [briefingKnowledgeIds, setBriefingKnowledgeIds] = useState(new Set());

  // Offer
  const [offerKeywords, setOfferKeywords] = useState('');
  const [offerTitle, setOfferTitle] = useState('Angebot zur Portooptimierung');
  const [offerIncludeSavings, setOfferIncludeSavings] = useState(true);
  const [offerDraft, setOfferDraft] = useState('');
  const [offerSavings, setOfferSavings] = useState(null);
  const [draftingOffer, setDraftingOffer] = useState(false);
  const [savingOffer, setSavingOffer] = useState(false);
  const [openOfferId, setOpenOfferId] = useState(null);
  const [offerKnowledgeIds, setOfferKnowledgeIds] = useState(new Set());

  // Save-as-template
  const [savingTemplateId, setSavingTemplateId] = useState(null);
  const [knowledgeRefresh, setKnowledgeRefresh] = useState(0);

  // Master-Data edit
  const [masterEdit, setMasterEdit] = useState(false);
  const [masterForm, setMasterForm] = useState({});
  const [savingMaster, setSavingMaster] = useState(false);

  const headers = useMemo(
    () => ({ 'x-admin-password': password }),
    [password]
  );

  const openMasterEdit = () => {
    setMasterForm({
      name: lead?.name || '',
      street: lead?.street || '',
      zip: lead?.zip || '',
      city: lead?.city || '',
      salutation: lead?.salutation || 'neutral',
      contact_person: lead?.contact_person || '',
      contact_role: lead?.contact_role || '',
      phone_office: lead?.phone_office || '',
      phone_mobile: lead?.phone_mobile || '',
      email1: lead?.email1 || '',
      email2: lead?.email2 || '',
      website: lead?.website || '',
    });
    setMasterEdit(true);
  };

  const saveMasterEdit = async () => {
    setSavingMaster(true);
    try {
      const res = await fetch(`${API}/admin/outreach/targets/${leadId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(masterForm),
      });
      const raw = await res.text();
      let data;
      try { data = JSON.parse(raw); } catch { data = {}; }
      if (!res.ok) throw new Error(data.detail || `Speichern fehlgeschlagen (HTTP ${res.status})`);
      await fetchLead();
      setMasterEdit(false);
    } catch (err) {
      console.error(err);
      alert(`Speichern fehlgeschlagen:\n\n${err.message}`);
    } finally {
      setSavingMaster(false);
    }
  };

  const fetchLead = async () => {
    try {
      const res = await fetch(`${API}/admin/outreach/targets/${leadId}`, { headers });
      if (!res.ok) throw new Error('Fehler beim Laden');
      const data = await res.json();
      setLead(data);
      setFollowUp(data.follow_up_date || '');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (leadId) fetchLead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const addActivity = async () => {
    if (!actContent.trim()) return;
    setSavingActivity(true);
    try {
      const res = await fetch(
        `${API}/admin/outreach/targets/${leadId}/activities`,
        {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: actType, content: actContent.trim(), date: actDate }),
        }
      );
      if (!res.ok) throw new Error('Aktivität konnte nicht gespeichert werden');
      setActContent('');
      setActDate(todayISO());
      await fetchLead();
      onChange?.();
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingActivity(false);
    }
  };

  const deleteActivity = async (activityId) => {
    if (!window.confirm('Aktivität löschen?')) return;
    try {
      await fetch(
        `${API}/admin/outreach/targets/${leadId}/activities/${activityId}`,
        { method: 'DELETE', headers }
      );
      fetchLead();
      onChange?.();
    } catch (err) {
      console.error(err);
    }
  };

  const saveFollowUp = async () => {
    setSavingFollowUp(true);
    try {
      await fetch(`${API}/admin/outreach/targets/${leadId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ follow_up_date: followUp || '' }),
      });
      await fetchLead();
      onChange?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingFollowUp(false);
    }
  };

  const generateBriefing = async () => {
    if (bKeywords.trim().length < 5) {
      alert('Bitte mindestens ein paar Stichworte zum Lead eingeben.');
      return;
    }
    setGenerating(true);
    try {
      const res = await fetch(
        `${API}/admin/outreach/targets/${leadId}/briefing`,
        {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            keywords: bKeywords.trim(),
            goal: bGoal.trim(),
            knowledge_ids: Array.from(briefingKnowledgeIds),
          }),
        }
      );
      // Body genau einmal als Text lesen, dann JSON parsen — verhindert "body stream already read".
      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch {
        throw new Error(
          `Server antwortete nicht im JSON-Format (HTTP ${res.status}). ` +
            `Erste 200 Zeichen: ${raw.slice(0, 200)}`
        );
      }
      if (!res.ok) {
        throw new Error(data.detail || `Briefing-Generierung fehlgeschlagen (HTTP ${res.status})`);
      }
      setBKeywords('');
      await fetchLead();
      setOpenBriefingId(data.id);
    } catch (err) {
      console.error('Briefing error:', err);
      alert(`Briefing konnte nicht erstellt werden:\n\n${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const deleteBriefing = async (bid) => {
    if (!window.confirm('Briefing löschen?')) return;
    try {
      await fetch(
        `${API}/admin/outreach/targets/${leadId}/briefing/${bid}`,
        { method: 'DELETE', headers }
      );
      fetchLead();
    } catch (err) {
      console.error(err);
    }
  };

  const downloadBriefing = (briefing) => {
    const blob = new Blob([briefing.content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safe = (lead?.name || 'lead').replace(/[^a-z0-9]+/gi, '-').slice(0, 50);
    a.download = `Briefing-${safe}-${briefing.created_at.slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---------- Offer handlers ----------
  const draftOffer = async () => {
    if (offerKeywords.trim().length < 5) {
      alert('Bitte mindestens ein paar Stichworte zum Angebot eingeben.');
      return;
    }
    setDraftingOffer(true);
    try {
      const res = await fetch(
        `${API}/admin/outreach/targets/${leadId}/offer-draft`,
        {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            keywords: offerKeywords.trim(),
            include_savings: offerIncludeSavings,
            knowledge_ids: Array.from(offerKnowledgeIds),
          }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Entwurf fehlgeschlagen');
      }
      const data = await res.json();
      setOfferDraft(data.draft);
      setOfferSavings(data.savings);
    } catch (err) {
      alert(err.message);
    } finally {
      setDraftingOffer(false);
    }
  };

  const saveOffer = async () => {
    if (!offerDraft.trim() || !offerTitle.trim()) {
      alert('Titel und Inhalt sind erforderlich.');
      return;
    }
    setSavingOffer(true);
    try {
      const res = await fetch(
        `${API}/admin/outreach/targets/${leadId}/offers`,
        {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: offerTitle.trim(),
            content: offerDraft,
            include_savings: offerIncludeSavings,
          }),
        }
      );
      if (!res.ok) throw new Error('Speichern fehlgeschlagen');
      const offer = await res.json();
      setOfferDraft('');
      setOfferKeywords('');
      setOfferSavings(null);
      await fetchLead();
      setOpenOfferId(offer.id);
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingOffer(false);
    }
  };

  const deleteOffer = async (offerId) => {
    if (!window.confirm('Angebot löschen?')) return;
    try {
      await fetch(
        `${API}/admin/outreach/targets/${leadId}/offers/${offerId}`,
        { method: 'DELETE', headers }
      );
      fetchLead();
    } catch (err) {
      console.error(err);
    }
  };

  const downloadOfferPdf = async (offer) => {
    try {
      const res = await fetch(
        `${API}/admin/outreach/targets/${leadId}/offers/${offer.id}/pdf`,
        { headers }
      );
      if (!res.ok) throw new Error('PDF-Generierung fehlgeschlagen');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (lead?.name || 'lead').replace(/[^a-z0-9]+/gi, '-').slice(0, 50);
      a.download = `Angebot-${safe}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    }
  };

  const saveAsTemplate = async (kind, content, suggestedTitle) => {
    const title = window.prompt(
      `Titel der Wissens-Vorlage:`,
      suggestedTitle || `${kind === 'offer' ? 'Angebot' : 'Briefing'} – ${lead?.name || ''}`,
    );
    if (!title || title.trim().length < 2) return;
    const tagsRaw = window.prompt(
      'Tags (Komma-getrennt, optional):',
      kind === 'offer' ? 'angebot,vorlage' : 'briefing,vorlage',
    );
    const tags = (tagsRaw || '').split(',').map((t) => t.trim()).filter(Boolean);
    setSavingTemplateId(kind);
    try {
      const res = await fetch(`${API}/admin/knowledge/items/from-text`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), notes: content, tags }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Speichern fehlgeschlagen');
      }
      alert(`„${title.trim()}" wurde im Wissens-Tab gespeichert und steht ab jetzt als Vorlage zur Verfügung.`);
      setKnowledgeRefresh((n) => n + 1);
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingTemplateId(null);
    }
  };

  if (!leadId) return null;

  const sortedActivities = lead?.activities
    ? [...lead.activities].sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    : [];
  const sortedBriefings = lead?.briefings
    ? [...lead.briefings].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
    : [];
  const sortedOffers = lead?.offers
    ? [...lead.offers].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
    : [];

  const fmtEUR = (n) =>
    typeof n === 'number'
      ? n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
      : '–';

  const websiteHref = (url) => {
    const u = (url || '').trim();
    if (!u) return '';
    if (/^https?:\/\//i.test(u)) return u;
    return `https://${u}`;
  };
  const phoneHref = (p) => `tel:${(p || '').replace(/[^+\d]/g, '')}`;
  const hasContact =
    lead && (lead.phone_office || lead.phone_mobile || lead.email1 || lead.email2 || lead.website);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid="lead-detail-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-8 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-6 py-4 flex items-center justify-between border-b border-slate-200"
          style={{ background: 'linear-gradient(90deg, rgba(255,162,102,0.15), rgba(255,162,102,0.05))' }}
        >
          <div>
            <h2 className="text-lg font-semibold text-slate-900" data-testid="lead-detail-name">
              {lead?.name || 'Lead-Details'}
            </h2>
            {lead && (
              <p className="text-xs text-slate-500 mt-0.5">
                {lead.city || ''} {lead.zip ? `· ${lead.zip}` : ''}
                {lead.contact_person ? ` · ${lead.contact_person}` : ''}
                {lead.contact_role ? ` (${lead.contact_role})` : ''}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-white/60 transition-colors"
            data-testid="lead-detail-close"
          >
            <X size={20} className="text-slate-600" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="animate-spin text-slate-400" size={28} />
          </div>
        ) : !lead ? (
          <div className="p-12 text-center text-slate-500">Lead nicht gefunden.</div>
        ) : (
          <div className="p-6 space-y-7">
            {/* Stammdaten — ganz oben, ohne scrollen beim Telefonieren */}
            <section
              className="bg-slate-50 border border-slate-200 rounded-lg p-4"
              data-testid="lead-master-data"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Kontaktdaten
                </h3>
                {!masterEdit ? (
                  <button
                    type="button"
                    onClick={openMasterEdit}
                    className="text-xs font-medium text-orange-700 hover:text-orange-800 inline-flex items-center gap-1"
                    data-testid="lead-master-edit-btn"
                  >
                    <Edit2 size={12} /> Bearbeiten
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setMasterEdit(false)}
                      className="text-xs text-slate-500 hover:text-slate-700"
                      data-testid="lead-master-cancel-btn"
                      disabled={savingMaster}
                    >
                      Abbrechen
                    </button>
                    <Button
                      onClick={saveMasterEdit}
                      disabled={savingMaster}
                      className="bg-orange-600 hover:bg-orange-700 text-white h-7 text-xs"
                      data-testid="lead-master-save-btn"
                    >
                      {savingMaster ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />}
                      Speichern
                    </Button>
                  </div>
                )}
              </div>
              {!masterEdit ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <div className="text-[11px] uppercase text-slate-400 tracking-wide">Einrichtung</div>
                    <div className="font-medium text-slate-900" data-testid="lead-master-name">
                      {lead.name || '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase text-slate-400 tracking-wide">Adresse</div>
                    <div className="text-slate-800" data-testid="lead-master-address">
                      {(lead.street || lead.zip || lead.city) ? (
                        <>
                          {lead.street && <div>{lead.street}</div>}
                          <div>
                            {[lead.zip, lead.city].filter(Boolean).join(' ')}
                          </div>
                        </>
                      ) : (
                        <span className="text-slate-400">— keine Adresse hinterlegt —</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase text-slate-400 tracking-wide">Ansprechpartner</div>
                    <div className="text-slate-800" data-testid="lead-master-contact">
                      {lead.contact_person ? (
                        <>
                          <div className="font-medium">{lead.contact_person}</div>
                          {lead.contact_role && (
                            <div className="text-xs text-slate-500">{lead.contact_role}</div>
                          )}
                        </>
                      ) : (
                        <span className="text-slate-400">— nicht erfasst —</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase text-slate-400 tracking-wide">Telefon (Büro)</div>
                    {lead.phone_office ? (
                      <a
                        href={phoneHref(lead.phone_office)}
                        className="inline-flex items-center gap-1.5 text-orange-700 hover:text-orange-800 font-medium text-base"
                        data-testid="lead-master-phone"
                      >
                        <Phone size={14} />
                        {lead.phone_office}
                      </a>
                    ) : (
                      <span className="text-slate-400">— nicht erfasst —</span>
                    )}
                    {lead.phone_mobile && (
                      <div className="text-xs text-slate-500 mt-1">
                        Mobil:{' '}
                        <a
                          href={phoneHref(lead.phone_mobile)}
                          className="text-slate-600 hover:text-orange-700"
                        >
                          {lead.phone_mobile}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-testid="lead-master-form">
                  <div className="md:col-span-2">
                    <label className="text-[11px] uppercase text-slate-500 tracking-wide font-medium">Einrichtung</label>
                    <Input
                      value={masterForm.name}
                      onChange={(e) => setMasterForm({ ...masterForm, name: e.target.value })}
                      className="mt-1"
                      data-testid="lead-master-input-name"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase text-slate-500 tracking-wide font-medium">Straße + Nr.</label>
                    <Input
                      value={masterForm.street}
                      onChange={(e) => setMasterForm({ ...masterForm, street: e.target.value })}
                      className="mt-1"
                      data-testid="lead-master-input-street"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[11px] uppercase text-slate-500 tracking-wide font-medium">PLZ</label>
                      <Input
                        value={masterForm.zip}
                        onChange={(e) => setMasterForm({ ...masterForm, zip: e.target.value })}
                        className="mt-1"
                        data-testid="lead-master-input-zip"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="text-[11px] uppercase text-slate-500 tracking-wide font-medium">Ort</label>
                      <Input
                        value={masterForm.city}
                        onChange={(e) => setMasterForm({ ...masterForm, city: e.target.value })}
                        className="mt-1"
                        data-testid="lead-master-input-city"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase text-slate-500 tracking-wide font-medium">Anrede</label>
                    <select
                      value={masterForm.salutation}
                      onChange={(e) => setMasterForm({ ...masterForm, salutation: e.target.value })}
                      className="mt-1 w-full border border-slate-300 rounded-md h-9 px-2 text-sm bg-white"
                      data-testid="lead-master-input-salutation"
                    >
                      <option value="neutral">neutral</option>
                      <option value="herr">Herr</option>
                      <option value="frau">Frau</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase text-slate-500 tracking-wide font-medium">Ansprechpartner</label>
                    <Input
                      value={masterForm.contact_person}
                      onChange={(e) => setMasterForm({ ...masterForm, contact_person: e.target.value })}
                      className="mt-1"
                      data-testid="lead-master-input-contact"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase text-slate-500 tracking-wide font-medium">Funktion / Rolle</label>
                    <Input
                      value={masterForm.contact_role}
                      onChange={(e) => setMasterForm({ ...masterForm, contact_role: e.target.value })}
                      placeholder="z. B. Verwaltungsdirektor"
                      className="mt-1"
                      data-testid="lead-master-input-role"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase text-slate-500 tracking-wide font-medium">Telefon (Büro)</label>
                    <Input
                      value={masterForm.phone_office}
                      onChange={(e) => setMasterForm({ ...masterForm, phone_office: e.target.value })}
                      placeholder="z. B. 0711-1234567 oder Durchwahl"
                      className="mt-1"
                      data-testid="lead-master-input-phone-office"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase text-slate-500 tracking-wide font-medium">Telefon (Mobil)</label>
                    <Input
                      value={masterForm.phone_mobile}
                      onChange={(e) => setMasterForm({ ...masterForm, phone_mobile: e.target.value })}
                      className="mt-1"
                      data-testid="lead-master-input-phone-mobile"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase text-slate-500 tracking-wide font-medium">E-Mail (primär)</label>
                    <Input
                      value={masterForm.email1}
                      onChange={(e) => setMasterForm({ ...masterForm, email1: e.target.value })}
                      className="mt-1"
                      data-testid="lead-master-input-email1"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase text-slate-500 tracking-wide font-medium">E-Mail (zusätzlich)</label>
                    <Input
                      value={masterForm.email2}
                      onChange={(e) => setMasterForm({ ...masterForm, email2: e.target.value })}
                      className="mt-1"
                      data-testid="lead-master-input-email2"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-[11px] uppercase text-slate-500 tracking-wide font-medium">Website</label>
                    <Input
                      value={masterForm.website}
                      onChange={(e) => setMasterForm({ ...masterForm, website: e.target.value })}
                      className="mt-1"
                      data-testid="lead-master-input-website"
                    />
                  </div>
                </div>
              )}
            </section>

            {/* Contact bar (Email, Website) */}
            {(lead.email1 || lead.email2 || lead.website) && (
              <section
                className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm"
                data-testid="lead-contact-bar"
              >
                {lead.email1 && (
                  <a
                    href={`mai` + `lto:${lead.email1}`}
                    className="inline-flex items-center gap-1.5 text-slate-700 hover:text-orange-600 transition-colors"
                    data-testid="lead-contact-email1"
                  >
                    <Mail size={13} className="text-slate-400" />
                    <span>{lead.email1}</span>
                  </a>
                )}
                {lead.email2 && (
                  <a
                    href={`mai` + `lto:${lead.email2}`}
                    className="inline-flex items-center gap-1.5 text-slate-700 hover:text-orange-600 transition-colors"
                    data-testid="lead-contact-email2"
                  >
                    <Mail size={13} className="text-slate-400" />
                    <span>{lead.email2}</span>
                  </a>
                )}
                {lead.website && (
                  <a
                    href={websiteHref(lead.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-slate-700 hover:text-orange-600 transition-colors"
                    data-testid="lead-contact-website"
                  >
                    <Globe size={13} className="text-slate-400" />
                    <span>{lead.website.replace(/^https?:\/\//i, '')}</span>
                  </a>
                )}
              </section>
            )}

            {/* Follow-up bar */}
            <section
              className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3"
              data-testid="lead-followup-section"
            >
              <Calendar size={18} className="text-amber-700" />
              <div className="text-sm font-medium text-amber-900 flex-1">
                Wiedervorlage:
              </div>
              <Input
                type="date"
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
                className="max-w-[180px] bg-white"
                data-testid="lead-followup-input"
              />
              <Button
                size="sm"
                onClick={saveFollowUp}
                disabled={savingFollowUp}
                className="bg-amber-700 hover:bg-amber-800 text-white"
                data-testid="lead-followup-save"
              >
                {savingFollowUp ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                <span className="ml-1.5">Speichern</span>
              </Button>
              {followUp && (
                <button
                  onClick={() => { setFollowUp(''); }}
                  className="text-xs text-amber-800 underline hover:text-amber-900"
                >
                  Datum löschen
                </button>
              )}
            </section>

            {/* Activities Section */}
            <section data-testid="lead-activities-section">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700 mb-3">
                Aktivitäten-Historie
              </h3>

              {/* Add activity */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-3 mb-4">
                <div className="flex flex-wrap gap-2">
                  {ACTIVITY_TYPES.map(({ value, label, Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setActType(value)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                        actType === value
                          ? 'border-slate-800 bg-slate-900 text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
                      }`}
                      data-testid={`activity-type-${value}`}
                    >
                      <Icon size={13} />
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2 items-start">
                  <Input
                    type="date"
                    value={actDate}
                    onChange={(e) => setActDate(e.target.value)}
                    className="max-w-[160px] bg-white"
                  />
                  <Textarea
                    placeholder="Was wurde besprochen / vereinbart?"
                    value={actContent}
                    onChange={(e) => setActContent(e.target.value)}
                    className="min-h-[60px] flex-1 bg-white"
                    data-testid="activity-content-input"
                  />
                  <Button
                    onClick={addActivity}
                    disabled={savingActivity || !actContent.trim()}
                    className="bg-slate-800 hover:bg-slate-700 text-white"
                    data-testid="activity-add-btn"
                  >
                    {savingActivity ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  </Button>
                </div>
              </div>

              {/* Timeline */}
              {sortedActivities.length === 0 ? (
                <p className="text-sm text-slate-400 italic px-2">
                  Noch keine Aktivitäten erfasst.
                </p>
              ) : (
                <ul className="space-y-2">
                  {sortedActivities.map((a) => {
                    const meta = ACTIVITY_TYPES.find((t) => t.value === a.type) || ACTIVITY_TYPES[4];
                    const Icon = meta.Icon;
                    return (
                      <li
                        key={a.id}
                        className="flex gap-3 items-start bg-white border border-slate-200 rounded-md p-3 hover:border-slate-300 transition-colors group"
                        data-testid={`activity-item-${a.id}`}
                      >
                        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                          <Icon size={15} className="text-slate-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                            <span className="font-medium text-slate-700">{meta.label}</span>
                            <span>·</span>
                            <span>{a.date}</span>
                          </div>
                          <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">
                            {a.content}
                          </p>
                        </div>
                        <button
                          onClick={() => deleteActivity(a.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded transition-all"
                          title="Löschen"
                        >
                          <Trash2 size={13} className="text-red-500" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Documents & Emails Section */}
            <DocumentsSection leadId={leadId} password={password} />

            {/* Outcome / Resonanz Section (Phase 6) */}
            <OutcomePanel lead={lead} password={password} onChange={fetchLead} />

            {/* Briefing Section */}
            <section data-testid="lead-briefing-section">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700 mb-1 flex items-center gap-2">
                <Sparkles size={14} className="text-orange-500" />
                Gesprächs-Briefing (KI-generiert)
              </h3>
              <p className="text-xs text-slate-500 mb-3">
                Stichworte zum Lead eingeben → Claude Sonnet 4.5 erstellt eine
                strukturierte 1–2-seitige Vorbereitung für dein Gespräch.
              </p>

              <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-3 mb-4">
                <Input
                  placeholder="Gesprächsziel (z. B. Erstgespräch vereinbaren, Angebot platzieren)"
                  value={bGoal}
                  onChange={(e) => setBGoal(e.target.value)}
                  data-testid="briefing-goal-input"
                />
                <Textarea
                  placeholder="Stichworte: Recherche-Ergebnisse, Aufhänger, bekannte Pain Points, Termin-Hintergrund …"
                  value={bKeywords}
                  onChange={(e) => setBKeywords(e.target.value)}
                  className="min-h-[80px]"
                  data-testid="briefing-keywords-input"
                />
                <Button
                  onClick={generateBriefing}
                  disabled={generating || bKeywords.trim().length < 5}
                  className="text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: (generating || bKeywords.trim().length < 5) ? '#cbd5e1' : '#FFA266',
                  }}
                  data-testid="briefing-generate-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 size={14} className="mr-2 animate-spin" />
                      Briefing wird erstellt — bitte Tab geöffnet lassen (ca. 45–60 Sek.) …
                    </>
                  ) : (
                    <>
                      <Sparkles size={14} className="mr-2" />
                      Briefing generieren
                    </>
                  )}
                </Button>
                <div className="pt-1">
                  <KnowledgePicker
                    password={password}
                    selectedIds={briefingKnowledgeIds}
                    onChange={setBriefingKnowledgeIds}
                    testIdPrefix="briefing"
                    refreshKey={knowledgeRefresh}
                  />
                </div>
              </div>

              {sortedBriefings.length === 0 ? (
                <p className="text-sm text-slate-400 italic px-2">
                  Noch keine Briefings generiert.
                </p>
              ) : (
                <ul className="space-y-2">
                  {sortedBriefings.map((b) => {
                    const open = openBriefingId === b.id;
                    return (
                      <li
                        key={b.id}
                        className="bg-white border border-slate-200 rounded-md overflow-hidden"
                        data-testid={`briefing-item-${b.id}`}
                      >
                        <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                          <button
                            onClick={() => setOpenBriefingId(open ? null : b.id)}
                            className="flex-1 flex items-center gap-2 text-left text-sm font-medium text-slate-800 hover:text-slate-900"
                          >
                            {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                            <span>
                              Briefing vom {new Date(b.created_at).toLocaleString('de-DE', {
                                day: '2-digit', month: '2-digit', year: 'numeric',
                                hour: '2-digit', minute: '2-digit',
                              })}
                            </span>
                            {b.goal && (
                              <span className="text-xs text-slate-500 font-normal italic">
                                · {b.goal}
                              </span>
                            )}
                          </button>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => downloadBriefing(b)}
                              className="p-1.5 hover:bg-white rounded transition-colors"
                              title="Als Markdown herunterladen"
                            >
                              <Download size={13} className="text-slate-600" />
                            </button>
                            <button
                              onClick={() => saveAsTemplate('briefing', b.content,
                                `Briefing-Vorlage – ${(lead?.type || '').replace('_', ' ')}`)}
                              disabled={savingTemplateId === 'briefing'}
                              className="p-1.5 hover:bg-orange-50 rounded transition-colors disabled:opacity-50"
                              title="Als Wissens-Vorlage speichern"
                              data-testid={`briefing-save-template-${b.id}`}
                            >
                              {savingTemplateId === 'briefing' ? (
                                <Loader2 size={13} className="animate-spin text-orange-600" />
                              ) : (
                                <BookmarkPlus size={13} className="text-orange-600" />
                              )}
                            </button>
                            <button
                              onClick={() => deleteBriefing(b.id)}
                              className="p-1.5 hover:bg-red-50 rounded transition-colors"
                              title="Briefing löschen"
                            >
                              <Trash2 size={13} className="text-red-500" />
                            </button>
                          </div>
                        </div>
                        {open && (
                          <div
                            className="px-5 py-4 markdown-briefing"
                            data-testid={`briefing-content-${b.id}`}
                          >
                            <ReactMarkdown>{b.content}</ReactMarkdown>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Offer Section */}
            <section data-testid="lead-offer-section">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700 mb-1 flex items-center gap-2">
                <FileSignature size={14} className="text-orange-500" />
                Angebots-Generator
              </h3>
              <p className="text-xs text-slate-500 mb-3">
                Stichworte → Claude Sonnet 4.5 erstellt einen Angebots-Entwurf
                inkl. Einsparindikation. Du kannst alles vor dem PDF-Download
                editieren.
              </p>

              <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-3 mb-4">
                <Input
                  placeholder="Titel des Angebots (z. B. Angebot Kurzanalyse)"
                  value={offerTitle}
                  onChange={(e) => setOfferTitle(e.target.value)}
                  data-testid="offer-title-input"
                />
                <Textarea
                  placeholder="Stichworte: Vorgespräch-Ergebnis, Pain Points, gewünschte Phasen / Aufwand, Investitionsrahmen …"
                  value={offerKeywords}
                  onChange={(e) => setOfferKeywords(e.target.value)}
                  className="min-h-[70px]"
                  data-testid="offer-keywords-input"
                />
                <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={offerIncludeSavings}
                    onChange={(e) => setOfferIncludeSavings(e.target.checked)}
                    className="rounded border-slate-300"
                    data-testid="offer-include-savings"
                  />
                  Einsparpotenzial-Box im PDF anzeigen (auf Basis Branchen-Heuristik)
                </label>
                <Button
                  onClick={draftOffer}
                  disabled={draftingOffer || offerKeywords.trim().length < 5}
                  className="text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: (draftingOffer || offerKeywords.trim().length < 5) ? '#cbd5e1' : '#FFA266',
                  }}
                  data-testid="offer-draft-btn"
                >
                  {draftingOffer ? (
                    <>
                      <Loader2 size={14} className="mr-2 animate-spin" />
                      Entwurf wird erstellt (15–25 Sek.) …
                    </>
                  ) : (
                    <>
                      <Sparkles size={14} className="mr-2" />
                      Entwurf generieren
                    </>
                  )}
                </Button>
                <div className="pt-1">
                  <KnowledgePicker
                    password={password}
                    selectedIds={offerKnowledgeIds}
                    onChange={setOfferKnowledgeIds}
                    testIdPrefix="offer"
                    refreshKey={knowledgeRefresh}
                  />
                </div>

                {offerDraft && (
                  <div className="border-t border-slate-200 pt-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-700 uppercase tracking-wider">
                        Entwurf — bearbeite frei vor dem Speichern
                      </span>
                      {offerSavings && offerSavings.savings_high > 0 && (
                        <span className="text-xs text-orange-700 bg-orange-50 px-2 py-0.5 rounded">
                          Einsparung indikativ: {fmtEUR(offerSavings.savings_low)} – {fmtEUR(offerSavings.savings_high)} / Jahr
                        </span>
                      )}
                    </div>
                    <Textarea
                      value={offerDraft}
                      onChange={(e) => setOfferDraft(e.target.value)}
                      className="min-h-[260px] font-mono text-xs"
                      data-testid="offer-draft-textarea"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={saveOffer}
                        disabled={savingOffer}
                        className="bg-slate-800 hover:bg-slate-700 text-white"
                        data-testid="offer-save-btn"
                      >
                        {savingOffer ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
                        Angebot speichern
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => { setOfferDraft(''); setOfferSavings(null); }}
                      >
                        Verwerfen
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {sortedOffers.length === 0 ? (
                <p className="text-sm text-slate-400 italic px-2">
                  Noch keine Angebote gespeichert.
                </p>
              ) : (
                <ul className="space-y-2">
                  {sortedOffers.map((o) => {
                    const open = openOfferId === o.id;
                    return (
                      <li
                        key={o.id}
                        className="bg-white border border-slate-200 rounded-md overflow-hidden"
                        data-testid={`offer-item-${o.id}`}
                      >
                        <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                          <button
                            onClick={() => setOpenOfferId(open ? null : o.id)}
                            className="flex-1 flex items-center gap-2 text-left text-sm font-medium text-slate-800 hover:text-slate-900"
                          >
                            {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                            <span>{o.title}</span>
                            <span className="text-xs text-slate-500 font-normal">
                              · {new Date(o.created_at).toLocaleDateString('de-DE')}
                            </span>
                            {o.include_savings && o.savings && o.savings.savings_high > 0 && (
                              <span className="text-xs text-orange-700 font-normal">
                                · {fmtEUR(o.savings.savings_low)}–{fmtEUR(o.savings.savings_high)} p.a.
                              </span>
                            )}
                          </button>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => downloadOfferPdf(o)}
                              className="p-1.5 hover:bg-white rounded transition-colors"
                              title="Als PDF herunterladen"
                              data-testid={`offer-pdf-${o.id}`}
                            >
                              <Download size={13} className="text-slate-700" />
                            </button>
                            <button
                              onClick={() => saveAsTemplate('offer', o.content,
                                `Angebots-Vorlage – ${o.title}`)}
                              disabled={savingTemplateId === 'offer'}
                              className="p-1.5 hover:bg-orange-50 rounded transition-colors disabled:opacity-50"
                              title="Als Wissens-Vorlage speichern"
                              data-testid={`offer-save-template-${o.id}`}
                            >
                              {savingTemplateId === 'offer' ? (
                                <Loader2 size={13} className="animate-spin text-orange-600" />
                              ) : (
                                <BookmarkPlus size={13} className="text-orange-600" />
                              )}
                            </button>
                            <button
                              onClick={() => deleteOffer(o.id)}
                              className="p-1.5 hover:bg-red-50 rounded transition-colors"
                              title="Angebot löschen"
                            >
                              <Trash2 size={13} className="text-red-500" />
                            </button>
                          </div>
                        </div>
                        {open && (
                          <div
                            className="px-5 py-4 markdown-briefing"
                            data-testid={`offer-content-${o.id}`}
                          >
                            <ReactMarkdown>{o.content}</ReactMarkdown>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadDetailModal;
